import * as React from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import useUserInfoStore from '@hooks/useUserInfoStore';
import { APR_USER_IDS } from '@utils/aprAccountUserIds';
import { postAppMessage } from '@utils/appMessage';
import useDeviceStore from '@hooks/useDeviceStore';
import { showSnsLinkBottomSheet } from '@components/bottomSheet/SnsLinkBottomSheet';
import ProfileImage from '@features/profile/[userId]/components/ProfileImage';
import Text from '@components/common/Text';
import { colorCode, font } from '@styles/styleUtils';
import { CMD } from '@src/constants/appMessageCmd';
import Icon from '@src/svg/Icon';

const Wrap = styled.div<{ $isSns: boolean }>`
  background: ${colorCode('gray90')};
  width: 100%;
  padding: ${({ $isSns }) => ($isSns ? '12px 16px' : '16px')};
  border-radius: 12px;
`;

const UserInfoWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  > div {
    display: flex;
    flex-direction: column;
  }
`;

const TextInfoWrap = styled.div`
  display: flex;
  flex-direction: column;

  > div {
    display: flex;
    align-items: center;
    gap: 6px;

    > p {
      display: flex;
      align-items: center;
      gap: 2px;
      color: ${colorCode('gray60')};
    }
  }
`;

const ProfileLink = styled(Link)`
  background: ${colorCode('gray80')};
  border: 1px solid ${colorCode('gray70')};
  color: ${colorCode('white')};
  padding: 4px 8px;
  border-radius: 999px;
  ${font('caption2_medium')};
  margin-left: auto;
`;

const SnsLinksWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-top: 1px solid ${colorCode('gray80')};
  color: ${colorCode('gray40')};
  margin-top: 14px;
  padding-top: 12px;
`;

interface Props {
  photoCardInfo?: PhotoCardType;
  triggerClickProfileGTM?: () => void;
  triggerClickSnsGTM?: (snsType: 'tiktok' | 'instagram') => void;
  winCountType?: 'DEFAULT' | 'HALL-OF-FAME';
}

const PhotoCardSnsLinks: React.FC<Props> = ({
  photoCardInfo,
  triggerClickProfileGTM,
  triggerClickSnsGTM,
  winCountType = 'DEFAULT',
}): JSX.Element => {
  const [userInfo] = useUserInfoStore();
  const [deviceInfo] = useDeviceStore();
  const isApp = !!deviceInfo?.deviceToken;

  const handleClickSnsButton = () => {
    if (photoCardInfo?.instagramProfile && photoCardInfo?.tiktokProfile) {
      showSnsLinkBottomSheet({
        instagramProfile: photoCardInfo.instagramProfile,
        tiktokProfile: photoCardInfo.tiktokProfile,
      });
      return;
    }
    if (photoCardInfo?.instagramProfile) {
      triggerClickSnsGTM?.('instagram');
      return isApp
        ? postAppMessage({
            cmd: CMD.SNS_CONNECT,
            type: 'external',
            url: photoCardInfo?.instagramProfile,
          })
        : window.open(photoCardInfo?.instagramProfile);
    }
    if (photoCardInfo?.tiktokProfile) {
      triggerClickSnsGTM?.('tiktok');
      return isApp
        ? postAppMessage({
            cmd: CMD.SNS_CONNECT,
            type: 'external',
            url: photoCardInfo?.tiktokProfile,
          })
        : window.open(photoCardInfo?.tiktokProfile);
    }
  };

  if (photoCardInfo?.userId && APR_USER_IDS.includes(photoCardInfo.userId)) {
    /* 비회원 혹은 마케팅 계정 */
    return <></>;
  }

  const isSns = !!(
    photoCardInfo?.instagramProfile || photoCardInfo?.tiktokProfile
  );

  return (
    <>
      <Wrap $isSns={isSns}>
        <UserInfoWrap>
          <ProfileImage
            profileImage={photoCardInfo?.userProfileImage}
            width={36}
            height={36}
          />
          <TextInfoWrap>
            <Text typo="body1_semiBold" color="white">
              {photoCardInfo?.photoCardName}
            </Text>
            <div>
              <p>
                <Icon name="card-crown" width={14} height={14} />
                <Text typo="caption1_medium" color="gray60">
                  우승{' '}
                  {winCountType === 'HALL-OF-FAME'
                    ? photoCardInfo?.hallOfFameWinCount?.toLocaleString()
                    : photoCardInfo?.winCount.toLocaleString()}
                  회
                </Text>
              </p>
              <p>
                <Icon name="card-heart" width={14} height={14} />
                <Text typo="caption1_medium" color="gray60">
                  Pick {photoCardInfo?.pickCount.toLocaleString()}회
                </Text>
              </p>
            </div>
          </TextInfoWrap>
          {photoCardInfo?.userId &&
            photoCardInfo.userId !== userInfo?.userId && (
              /* 다른 유저의 카드일때만 표시 */
              <ProfileLink
                href={`/profile/${photoCardInfo?.userId}`}
                onClick={triggerClickProfileGTM}
              >
                프로필보기
              </ProfileLink>
            )}
        </UserInfoWrap>
        {isSns && (
          <SnsLinksWrap onClick={handleClickSnsButton}>
            {photoCardInfo?.instagramProfile && (
              <Icon name="instagram" width={16} height={16} />
            )}
            {photoCardInfo?.tiktokProfile && (
              <Icon name="tiktok" width={16} height={16} />
            )}
            <Text typo="body2_medium">SNS 방문하기</Text>
            <Icon name="arrow-right" width={16} height={16} />
          </SnsLinksWrap>
        )}
      </Wrap>
    </>
  );
};

export default PhotoCardSnsLinks;
